import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { getEmbedPayload } from "rc-app/api/embedActionsApi";
import { resolveOpenMode, opensFullscreen } from "rc-app/utils/publishEmbedFlow";
import { MATTERPORT_SDK_KEY, useMatterportAnalytics } from "rc-app/hooks/useMatterportAnalytics";
import MatterportLeadGate from "rc-app/components/MatterportLeadGate";

/* ── The embed page — what actually loads inside a customer's website ───
   Spec: WEBSITE_EMBED_SPEC.md §10.4. Route `/e/:key`, declared OUTSIDE <Layout>:
   no header, no drawer, no login. The visitor is anonymous and on someone
   else's domain.

   ── A THIN WRAPPER, NOT A RENDERER ──
   It composes the asset's own URL with the same params ProjectDetailsV3 already
   appends, then iframes it. The engine and the Matterport viewer are unchanged
   and unaware they're inside an embed.

   ── THE NESTED-IFRAME GOTCHA (§10.5) ──
   There are two levels of framing, three for a twin:

     developer's page
       └─ iframe: this page          <- their snippet controls this `allow`
            └─ iframe: the asset     <- OURS, below
                 └─ Matterport       <- twins only

   `allow` does NOT inherit downward. The permission list has to be repeated on
   our inner iframe or fullscreen and the motion sensors silently die inside an
   embed while working perfectly everywhere else.

   ── LEAD ATTRIBUTION, AND WHAT IS STILL MISSING ──
   `send_otp` stamps a lead with `embed_key` / `embed_site_id` by looking them up
   on the VR SESSION, via `session_id`. The twin gate has that id, because
   `useMatterportAnalytics` starts the session here. The meta-model session is
   started by the ENGINE, inside the iframe, so we never see its id — which means
   a meta-model lead currently lands correctly in the agency but WITHOUT the
   embed source and site.
   Two ways to close it, both needing a call with the engine side: have the
   engine forward `embed_key` onto its own session/start, or start the session
   from this page and stop the engine starting one. Until then the gate captures
   the lead; only the attribution is thin.

   ── WHY THE NOTICES ARE DELIBERATELY VAGUE ──
   This page renders on a domain we don't control, to visitors who aren't our
   users. A 404 from the backend is one opaque answer covering unknown, revoked,
   unpublished and off-allowlist — telling a stranger which of those it is would
   confirm whether a key exists. So the page says the same neutral thing for all
   of them, and never renders a stack trace or an id. */

const ALLOW = "fullscreen; xr-spatial-tracking; accelerometer; gyroscope; magnetometer; camera; microphone";

/* The visitor's campaign tags, forwarded onto OUR url by the snippet.

   An iframe cannot read its parent's location, so a buyer who reached the developer's page
   from a paid ad is, from in here, indistinguishable from someone who typed the address. The
   snippet copies utm_* across; this reads them back, the session carries them, and the CRM
   reports Source / Sub source from utm_source / utm_medium (Ankura's convention).

   Whitelisted and length-capped on purpose: these arrive on a URL a stranger controls and end
   up in an agency's CRM. */
const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"];

const readUtm = (): Record<string, string> => {
  const out: Record<string, string> = {};
  try {
    const q = new URLSearchParams(window.location.search);
    UTM_KEYS.forEach((k) => {
      const v = (q.get(k) || "").trim();
      if (v) out[k] = v.slice(0, 200);
    });
  } catch {
    /* no URLSearchParams, or no window — nothing to forward */
  }
  return out;
};

/* How long a meta-model visitor explores before the gate appears.
   The twin gates on SWEEPS VISITED — real engagement, straight from the
   Matterport SDK. A meta model is a plain cross-origin iframe and the engine
   posts nothing to its parent (checked: no postMessage to window.parent
   anywhere in opensky-vr-engine), so from out here we cannot tell a buyer
   walking three villas from a tab left open on a desk.
   Time is the only signal available, so the gate leans on `evaluateTrigger`'s
   max-wait fallback with no node target. It is deliberately generous — gating
   someone who has barely looked is worse than gating late.
   Replace this with `controller.on('scene.enter')` from the Experience SDK the
   day that lands; it is the same trigger the twin already uses, and it is the
   real reason the SDK is worth integrating. */
const METAMODEL_DWELL_MS = 45_000;

type Status = "loading" | "ready" | "gone" | "forbidden" | "error" | "direct";

interface Payload {
  project_id?: string;
  project_slug?: string;
  project_name?: string;
  agency_id?: string;
  agency_name?: string;
  agency_logo_url?: string;
  experience_kind?: string;
  experience_id?: string;
  experience_slug?: string;
  experience_url?: string;
  meta_model_id?: string;
  poster_url?: string | null;
  lead_capture?: boolean;
  site_host?: string;
  /**
   * "fullscreen" | "inline" — what the first tap does. Set per placement in the
   * Publish modal. ABSENT means nobody chose, and absent must stay absent:
   * defaulting it to "fullscreen" server-side would override every `?fs=0`
   * already pasted on a live page. See `resolveOpenMode`.
   */
  open_mode?: string | null;
}

/** Full-bleed neutral notice. No branding, no detail — see the header note. */
const Notice: React.FC<{ title: string; body: string }> = ({ title, body }) => (
  <div
    style={{
      position: "fixed",
      inset: 0,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      background: "#0d0f12",
      color: "#c9ced6",
      font: "400 15px/1.6 system-ui, -apple-system, Segoe UI, Roboto, sans-serif",
      textAlign: "center",
      padding: 24,
    }}
  >
    <div style={{ maxWidth: 380 }}>
      <div style={{ fontSize: 17, fontWeight: 600, color: "#e8ecf1", marginBottom: 8 }}>{title}</div>
      <div>{body}</div>
    </div>
  </div>
);

/**
 * Is this page inside a frame at all?
 *
 * `frame-ancestors` decides WHICH sites may frame us, but says nothing about
 * opening the URL directly in a tab — and the src is visible in the page source
 * of every site that embeds it, so it will get copied. On its own that URL
 * should be inert: the embed is a component of someone's website, not a
 * shareable link to the experience. Share links are a separate product with
 * their own attribution (/s/:code).
 *
 * Cross-origin access to window.top throws, and a throw means we ARE framed —
 * so the catch is the success path, not an error case.
 */
const isFramed = (): boolean => {
  try {
    return window.self !== window.top;
  } catch {
    return true;
  }
};

const GOLD = "#E8B96B";

/**
 * A real photograph of the space, straight from Matterport.
 *
 * `/api/v1/player/models/<sid>/thumb` 302s to a full-size still of the model —
 * public, unauthenticated, and sized by query param. It needs no backend field
 * and no capture work, which matters because `Matterport` has no
 * `thumbnail_url` column and a composed placeholder is never going to make
 * anyone click.
 *
 * The redirect target is signed and expires within a day, so this must stay
 * pointed at the /thumb URL and let the browser follow it each load — caching
 * the resolved CDN link would go dead overnight.
 */
const twinPoster = (matterportId?: string): string =>
  matterportId
    ? `https://my.matterport.com/api/v1/player/models/${encodeURIComponent(matterportId)}/thumb?width=1280&height=720`
    : "";

/**
 * What a visitor sees before they ask for anything — and the only thing
 * standing between a builder's page and a click.
 *
 * A dark rectangle with a grey circle reads as something broken. A photograph
 * of the actual room, a warm play control and the room's name reads as an
 * invitation. The photo is the whole design; everything else sits on top of it.
 *
 * Falls back to the composed gradient if the image fails — an embed on someone
 * else's site must never show a broken-image icon.
 */
const Poster: React.FC<{
  projectName?: string;
  assetName?: string;
  agencyLogoUrl?: string;
  kind?: string;
  posterUrl?: string;
  onStart: () => void;
}> = ({ projectName, assetName, agencyLogoUrl, kind, posterUrl, onStart }) => {
  const [imgOk, setImgOk] = useState(true);
  const [hover, setHover] = useState(false);
  const isTwin = kind === "digital_twin";
  const showImg = !!posterUrl && imgOk;

  return (
    <button
      type="button"
      onClick={onStart}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      aria-label={`Start the ${assetName || projectName || "walkthrough"} tour`}
      style={{
        position: "absolute", inset: 0, width: "100%", height: "100%",
        border: 0, padding: 0, cursor: "pointer", overflow: "hidden",
        display: "block", textAlign: "left",
        background: "radial-gradient(120% 90% at 50% 0%, #223 0%, #11131a 55%, #0b0d12 100%)",
        font: "500 15px/1.5 system-ui, -apple-system, Segoe UI, Roboto, sans-serif",
      }}
    >
      {showImg && (
        <img
          src={posterUrl}
          alt=""
          onError={() => setImgOk(false)}
          style={{
            position: "absolute", inset: 0, width: "100%", height: "100%",
            objectFit: "cover", display: "block",
            // A slow push-in on hover. Enough to feel alive, not enough to
            // distract from the page it is sitting on.
            transform: hover ? "scale(1.045)" : "scale(1)",
            transition: "transform .7s cubic-bezier(.2,.8,.2,1)",
          }}
        />
      )}

      {/* Scrim. Dark at the bottom where the words are, lifted in the middle so
          the play control stays legible over a bright room. */}
      <span
        aria-hidden
        style={{
          position: "absolute", inset: 0,
          background:
            "linear-gradient(to top, rgba(6,8,12,.92) 0%, rgba(6,8,12,.45) 34%, rgba(6,8,12,.12) 62%, rgba(6,8,12,.35) 100%)",
        }}
      />

      {agencyLogoUrl ? (
        <img
          src={agencyLogoUrl}
          alt=""
          style={{
            position: "absolute", top: 16, left: 18, height: 22, width: "auto",
            objectFit: "contain", opacity: .9, maxWidth: "44%",
            filter: "drop-shadow(0 2px 6px rgba(0,0,0,.6))",
          }}
        />
      ) : null}

      {/* The play control, centred. Gold rather than grey: it is the one thing
          on this page asking to be pressed, and it carries the same colour the
          lead gate uses, so the experience feels of a piece. */}
      <span
        aria-hidden
        style={{
          position: "absolute", top: "50%", left: "50%",
          transform: `translate(-50%,-50%) scale(${hover ? 1.07 : 1})`,
          transition: "transform .25s cubic-bezier(.2,.8,.2,1), box-shadow .25s",
          width: 76, height: 76, borderRadius: "50%",
          display: "grid", placeItems: "center",
          background: `linear-gradient(140deg, #F2CE86, ${GOLD})`,
          boxShadow: hover
            ? `0 14px 40px -10px rgba(0,0,0,.75), 0 0 0 10px rgba(232,185,107,.16)`
            : `0 14px 40px -12px rgba(0,0,0,.7), 0 0 0 6px rgba(232,185,107,.10)`,
        }}
      >
        {/* Nudged right: a triangle centred on its bounding box always reads as
            sitting left of centre inside a circle. */}
        <svg width="27" height="27" viewBox="0 0 24 24" fill="#17121f" style={{ marginLeft: 4 }}>
          <path d="M8 5.2v13.6a.8.8 0 0 0 1.22.68l10.5-6.8a.8.8 0 0 0 0-1.36L9.22 4.52A.8.8 0 0 0 8 5.2z" />
        </svg>
      </span>

      {/* Words at the bottom, where they sit over the darkest part of the scrim
          and never fight the photograph. */}
      <span
        style={{
          position: "absolute", left: 20, right: 20, bottom: 18,
          display: "flex", flexDirection: "column", gap: 3,
        }}
      >
        <span
          style={{
            fontSize: 11, letterSpacing: ".13em", textTransform: "uppercase",
            color: GOLD, fontWeight: 700,
          }}
        >
          {isTwin ? "Step inside" : "Explore"}
        </span>
        <span
          style={{
            fontSize: 21, lineHeight: 1.2, fontWeight: 700, color: "#fff",
            textShadow: "0 2px 12px rgba(0,0,0,.55)",
          }}
        >
          {assetName || projectName || "Immersive walkthrough"}
        </span>
        <span style={{ fontSize: 12.5, color: "rgba(255,255,255,.72)" }}>
          {isTwin
            ? "Walk the finished home · 3D tour"
            : "Walk the community · 3D walkthrough"}
        </span>
      </span>
    </button>
  );
};

const EmbedPage: React.FC = () => {
  const { key = "" } = useParams<{ key: string }>();
  const [status, setStatus] = useState<Status>("loading");
  const [data, setData] = useState<Payload>({});
  const twinRef = useRef<HTMLIFrameElement | null>(null);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  /* Nothing heavy mounts until the visitor asks — see the Poster note. */
  const [started, setStarted] = useState(false);
  const [utm] = useState<Record<string, string>>(readUtm);
  const [hint, setHint] = useState(false);

  /* Only offer the control if the browser will actually grant it. The host page
     controls this via `allow="fullscreen"` on their iframe, and a customer who
     pasted a trimmed snippet would otherwise get a button that does nothing. */
  const canFullscreen =
    typeof document !== "undefined" &&
    (document.fullscreenEnabled || (document as any).webkitFullscreenEnabled);

  useEffect(() => {
    const onChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onChange);
    return () => document.removeEventListener("fullscreenchange", onChange);
  }, []);

  const toggleFullscreen = () => {
    const el: any = wrapRef.current;
    if (!el) return;
    if (document.fullscreenElement) {
      void document.exitFullscreen?.();
      return;
    }
    // Safari on iPhone has neither, and refuses element fullscreen outright —
    // the optional calls keep that a no-op rather than a thrown error.
    void (el.requestFullscreen?.() ?? el.webkitRequestFullscreen?.());
  };

  /* Fullscreen on the first tap, or play in the page. The customer's choice per
     placement; `?fs=0` is the fallback until the field exists on the key. */
  const openMode = resolveOpenMode(
    data.open_mode,
    typeof window !== "undefined" ? window.location.search : "",
  );

  /* Read once: neither the pointer type nor the screen size changes under a
     visitor mid-session, and `screen` is the DEVICE, not this iframe's box —
     which is the whole point, since our box is whatever the host page gave us. */
  const [device] = useState(() => {
    if (typeof window === "undefined") return { coarsePointer: false, minScreenPx: 0 };
    let coarse = false;
    try { coarse = window.matchMedia("(pointer: coarse)").matches; } catch { /* old browser */ }
    const s = window.screen || ({} as Screen);
    return {
      coarsePointer: coarse,
      minScreenPx: Math.min(s.width || 0, s.height || 0),
    };
  });
  /* A phone overrides the setting — see `opensFullscreen`. */
  const goFullscreen = opensFullscreen(openMode, device);

  /**
   * The tap that starts everything: mount the iframe, maybe go fullscreen, show
   * the hint once.
   *
   * Fullscreen is requested from inside THIS click handler because browsers
   * only grant it during a user gesture — which is also why it can't be done
   * on load, and why the poster is what makes fullscreen possible at all
   * rather than merely nicer.
   *
   * On iPhone it simply won't happen: Safari supports fullscreen for video
   * only. The tour still opens, at whatever height the snippet's min-height
   * floor gave it, and the gate's own short-viewport rules take it from there.
   * So this is an enhancement, never a dependency — which is also the honest
   * reason the inline mode exists: on a landing page carrying specs and an
   * enquiry form, seizing the screen throws the visitor out of the page the
   * developer paid to build, and on iPhone it was never seizing it anyway.
   *
   * Either way the ⤢ control below stays, so this decides only the FIRST tap.
   *
   * A PHONE IGNORES THE SETTING and always asks for fullscreen: the only reason
   * to keep a walkthrough in the page is that something sits beside it, and on a
   * phone nothing does — everything is one stacked column. See `opensFullscreen`.
   */
  const start = () => {
    setStarted(true);
    if (goFullscreen) {
      const el: any = wrapRef.current;
      try {
        void (el?.requestFullscreen?.() ?? el?.webkitRequestFullscreen?.());
      } catch {
        // Denied or unsupported — the tour still plays inline.
      }
    }
    setHint(true);
    window.setTimeout(() => setHint(false), 4200);
  };

  useEffect(() => {
    if (!key) { setStatus("gone"); return undefined; }
    // Opened directly rather than embedded — say nothing and load nothing. No
    // render call either, so a copied URL can't even be used to probe whether a
    // key is live.
    if (!isFramed()) { setStatus("direct"); return undefined; }
    let cancelled = false;
    getEmbedPayload(key)
      .then((p: Payload) => {
        if (cancelled) return;
        setData(p || {});
        setStatus("ready");
      })
      .catch((err: any) => {
        if (cancelled) return;
        const code = err?.response?.status;
        // 403 = this key isn't allowed on this site. 404 = the opaque
        // unknown/unpublished/revoked answer. Anything else is ours, not theirs.
        setStatus(code === 403 ? "forbidden" : code === 404 ? "gone" : "error");
      });
    return () => { cancelled = true; };
  }, [key]);

  const isTwin = data.experience_kind === "digital_twin";
  const gateOn = status === "ready" && started && data.lead_capture !== false;

  /* The twin's session + sweep count. Armed only for a live twin, so a meta-model
     embed never opens a Matterport session it has no use for. */
  const mp = useMatterportAnalytics(twinRef, status === "ready" && started && isTwin, {
    matterport: { matterport_id: data.experience_id },
    projectId: data.project_id,
    agencyId: data.agency_id,
    agencySlug: undefined,
    projectSlug: data.project_slug,
    clientManagerId: undefined,
    clientAdminId: undefined,
    agentId: undefined,
    customerId: undefined,
    // The one field that makes this an EMBED session rather than an anonymous
    // tour. Missing until 2026-09-17: the twin gate had a session id, so the
    // lead was captured, but the session never said it came from an embed —
    // no `source = embed`, no originating site, and the SMS fell back to the
    // generic login text even after the backend learned the embed template.
    embedKey: key,
    // The visitor's own campaign tags, forwarded by the snippet from the host page.
    utm,
  } as any);

  const src = useMemo(() => {
    if (status !== "ready") return "";
    if (isTwin) {
      if (!data.experience_id) return "";
      // Same params as the project page: mls=1 keeps Matterport inside the frame
      // on mobile instead of navigating the parent, qs=1 hides the quick-start
      // panel, play=1 enters the tour on load.
      return `https://my.matterport.com/show/?m=${encodeURIComponent(data.experience_id)}&applicationKey=${MATTERPORT_SDK_KEY}&mls=1&qs=1&play=1`;
    }
    // Meta-model urls are stored bare ("nandanavanamtest.netlify.app"), so the
    // scheme has to be put back. Without it the browser reads the src as a
    // RELATIVE path and resolves it against our own host — the iframe silently
    // loads nothing. ProjectDetailsV3 does the same normalisation.
    const raw = String(data.experience_url || "").trim().replace(/\/+$/, "");
    if (!raw) return "";
    const base = /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
    const q = [
      data.project_id ? `projectId=${encodeURIComponent(data.project_id)}` : "",
      data.agency_id ? `agencyId=${encodeURIComponent(data.agency_id)}` : "",
      data.project_slug ? `project_slug=${encodeURIComponent(data.project_slug)}` : "",
      data.meta_model_id ? `metaModelId=${encodeURIComponent(data.meta_model_id)}` : "",
      data.experience_slug ? `experience_slug=${encodeURIComponent(data.experience_slug)}` : "",
      `experience_type=m`,
      // ALWAYS 0 — the platform gates now, not the engine. The engine's own
      // gate is on its way out, but it is still live in deployed builds, and
      // leaving it armed would gate the same visitor twice: once from inside
      // the iframe and once from this page. Our gate is the authoritative one
      // (it is the only one that exists for twins), so the engine stands down
      // regardless of the site's lead_capture setting.
      `leadgate=0`,
      // Attribution: the engine forwards unknown params onto /session/start,
      // where the backend reads embed_key and stamps the placement + its site.
      `embed_key=${encodeURIComponent(key)}`,
    ].filter(Boolean).join("&");
    return `${base}/?${q}`;
  }, [status, isTwin, data, key]);

  if (status === "loading") return <Notice title="Loading…" body="One moment." />;
  // Deliberately says nothing about keys, embeds or where it belongs — someone
  // who pasted the URL learns only that there is nothing here for them.
  if (status === "direct")
    return <Notice title="Nothing to see here" body="This page is part of another website." />;
  if (status === "forbidden")
    return <Notice title="Not available here" body="This experience isn’t available on this website." />;
  if (status === "gone")
    return <Notice title="No longer available" body="This experience is no longer available." />;
  if (status === "error")
    return <Notice title="Something went wrong" body="Please try again in a moment." />;
  if (!src)
    return <Notice title="Nothing to show" body="This experience isn’t ready yet." />;

  return (
    <div ref={wrapRef} style={{ position: "fixed", inset: 0, background: "#000", overflow: "hidden" }}>
      {/* Nothing loads until they ask for it. The iframe is mounted by `started`,
          so a visitor scrolling past a builder's page costs no Matterport
          session, no SDK, no tiles — and never sees the 2-3s black rectangle
          that connecting leaves behind.
          It also makes the numbers honest: a session now means intent, not a
          scroll-past, so view -> engage -> lead is a real funnel. */}
      {started ? (
        <iframe
          ref={isTwin ? twinRef : undefined}
          src={src}
          title={data.project_name || "Immersive walkthrough"}
          style={{ position: "absolute", inset: 0, width: "100%", height: "100%", border: 0 }}
          // Repeated from the customer's outer snippet on purpose — see §10.5.
          allow={ALLOW}
          allowFullScreen
        />
      ) : (
        <Poster
          projectName={data.project_name}
          // No asset name on the wire yet — the placement's `label` ("Konak
          // Villa 10") exists on the key but isn't in the render payload, so
          // the project name stands in. Worth asking for: the villa is what a
          // buyer is looking at, not the project.
          assetName={undefined}
          agencyLogoUrl={data.agency_logo_url}
          kind={data.experience_kind}
          // Twins get a real photo of the space. Meta models get the payload's
          // poster_url (the model's thumbnail, else the project's first image),
          // and keep the composed fallback when neither exists.
          posterUrl={isTwin ? twinPoster(data.experience_id) : data.poster_url || undefined}
          onStart={start}
        />
      )}
      {/* A hint, not a tutorial. Shows once on entry and fades on its own —
          nobody reads an overlay they didn't ask for, and one that needs
          dismissing is worse than none. */}
      {hint && (
        <div
          style={{
            position: "absolute", left: "50%", bottom: 18, transform: "translateX(-50%)",
            zIndex: 4, pointerEvents: "none", maxWidth: "88%",
            padding: "8px 14px", borderRadius: 999,
            background: "rgba(12,14,18,.62)", color: "#e8ecf1",
            font: "500 12.5px/1.4 system-ui, -apple-system, Segoe UI, Roboto, sans-serif",
            border: "1px solid rgba(255,255,255,.14)", textAlign: "center",
            backdropFilter: "blur(8px)", WebkitBackdropFilter: "blur(8px)",
            animation: "embedHint .4s ease both",
          }}
        >
          {/* In the page, the hint is the only thing that tells them fullscreen
              exists at all — the ⤢ sits quietly in a corner and reads as chrome. */}
          {isFullscreen
            ? "Drag to look around · Esc or ⤢ to exit"
            : canFullscreen
              ? "Drag to look around · ⤢ for fullscreen"
              : "Drag to look around"}
        </div>
      )}
      <style>{"@keyframes embedHint{from{opacity:0;transform:translate(-50%,8px)}to{opacity:1;transform:translate(-50%,0)}}"}</style>
      {/* Fullscreen, ours rather than the viewer's.
          Matterport and the engine each have their own control, but they sit at
          different corners and only appear once their runtime has loaded — so
          inside a 16:9 slot on someone's page there is a long moment with no way
          to go big, and on a phone that slot is small. Requesting it on the
          WRAPPER rather than the iframe takes the gate fullscreen too; otherwise
          the gate would render behind the fullscreen element and be invisible
          exactly when a visitor is most engaged.
          The button hides itself if the browser won't allow it — an embed whose
          host page omitted `allow="fullscreen"` should show no dead control. */}
      {canFullscreen && started && (
        <button
          type="button"
          onClick={toggleFullscreen}
          aria-label={isFullscreen ? "Exit fullscreen" : "View fullscreen"}
          title={isFullscreen ? "Exit fullscreen" : "View fullscreen"}
          style={{
            position: "absolute", top: 12, right: 12, zIndex: 3,
            width: 38, height: 38, borderRadius: 9,
            border: "1px solid rgba(255,255,255,.28)",
            background: "rgba(12,14,18,.55)", color: "#fff",
            cursor: "pointer", display: "grid", placeItems: "center",
            backdropFilter: "blur(6px)", WebkitBackdropFilter: "blur(6px)",
            padding: 0, lineHeight: 0,
          }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
               stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round">
            {isFullscreen
              ? <><path d="M9 3v6H3" /><path d="M15 21v-6h6" /><path d="M21 9h-6V3" /><path d="M3 15h6v6" /></>
              : <><path d="M3 9V3h6" /><path d="M21 9V3h-6" /><path d="M3 15v6h6" /><path d="M21 15v6h-6" /></>}
          </svg>
        </button>
      )}
      {/* ONE gate, both asset kinds — the platform owns lead capture now.
          It used to be split: the twin gated here (Matterport has no gate of its
          own) while the meta model gated INSIDE the engine, armed by `leadgate=1`
          on its URL. That engine gate is being stripped so capture lives in one
          place, which means this component has to cover both or meta-model
          embeds would silently collect nothing.
          The only difference between them is the trigger: the twin has a real
          engagement signal (sweeps), the meta model has a timer. */}
      {gateOn && data.agency_id && (
        <MatterportLeadGate
          key={`embed-${data.experience_id}`}
          enabled
          agencyId={data.agency_id}
          projectKey={data.project_slug || data.project_id || ""}
          projectName={data.project_name}
          agencyName={data.agency_name}
          agencyLogoUrl={data.agency_logo_url}
          // Twin only. The meta-model session is started by the engine, so we
          // don't hold its id — see the attribution note in the header.
          sessionId={isTwin ? mp.sessionId || undefined : undefined}
          // Both kinds. For a meta model this is the ONLY thing that marks its
          // lead as an embed lead; for a twin the session already carries it.
          embedKey={key}
          // Same reasoning, same blind spot: the session carries these for a twin, and a
          // meta model has no session — so without this a paid click on a meta-model
          // embed reaches the CRM with the agency's default Source and files as organic.
          utm={utm}
          sweepCount={isTwin ? mp.sweepsVisited : 0}
          totalSpaces={isTwin ? mp.totalSweeps || undefined : undefined}
          // 0 would be "already reached" and fire the gate instantly, so the
          // meta model keeps the default node target it can never hit and
          // falls through to the max-wait timer instead.
          maxWaitMs={isTwin ? undefined : METAMODEL_DWELL_MS}
          // The gate normally waits for a session or a sweep before it starts
          // counting. A meta model produces neither — the engine owns its
          // session and tells us nothing — so without this the dwell clock
          // never starts and the gate never appears at all.
          armOnMount={!isTwin && started}
        />
      )}
    </div>
  );
};

export default EmbedPage;
